import { ErrorCode, type ApiEnvelope } from '@trulyinbox/errors';
import { apiFetch, ApiError } from './api';
import { env } from './env';
import { resolvedTimeZone } from './date';
import { tokenStorage } from './storage-tokens';
import type { TagColorKey } from './tag-palette';

/**
 * A workspace tag as assigned to an email account. The backend stores a palette
 * KEY (one of `TagColorKey`) in `color`, not a hex value — see `tag-palette.ts`.
 */
export type Tag = {
  id: number;
  name: string;
  color: TagColorKey;
};

/** A workspace tag from `GET /tags` — the catalog row with its usage count. */
export type WorkspaceTag = Tag & {
  /** How many accounts in the workspace currently carry this tag. */
  accountCount: number;
};

export type SmtpEncryption = 'SSL' | 'TLS' | 'NONE';

/**
 * The coarse warmup-STATE bucket an account's canonical 19-state `accountState`
 * collapses to (the `?status=` filter vocabulary, plus the internal `setup`
 * bucket for connection states). Mirrors `StatusBucket` in
 * `@trulyinbox/types` (`account-state.enum.ts`).
 *
 * Local copy (not imported from `@trulyinbox/types`) because that package is
 * not a dependency of `apps/web` — only `@trulyinbox/errors` is wired into the
 * tsconfig paths / vite resolution. Keep this in lock-step with the backend
 * union; one new bucket is one edit here.
 */
export type StatusBucket =
  | 'active'
  | 'throttled'
  | 'paused'
  | 'suspended'
  | 'cooldown'
  | 'maintenance'
  | 'disconnected'
  | 'setup';

/**
 * Human- and machine-readable explanation of an account's current warmup state,
 * surfaced from the latest transition's audit row (plan §3.4). Mirrors
 * `StructuredStatus` in `@trulyinbox/types` (`warmup.interface.ts`); see the
 * note on `StatusBucket` above for why it's a local copy.
 *
 * `severity` drives the detail-page banner tone; `recovery_eta` is an ISO
 * datetime (the auto-resume ETA) or null; `next_step` is a human instruction.
 */
export type StructuredStatus = {
  /** What happened, in plain language (the status title). */
  what: string;
  /** Why it happened (the status body). */
  why: string;
  /** Which subsystem/actor produced the transition. */
  source: string;
  /** Who owns the next action (user / system / provider). */
  owner: string;
  /** The recommended next step (a human instruction). */
  next_step: string;
  /** Optional deep link the owner can follow to resolve. */
  action_url?: string | null;
  severity: 'info' | 'warning' | 'error';
  /** Whether the system will recover automatically without user action. */
  auto_recovery: boolean;
  /** Estimated recovery time — an ISO datetime string (auto-resume ETA) or null. */
  recovery_eta?: string | null;
};

export type ConnectSmtpPayload = {
  emailAddress: string;
  /** Written to `emailAccountWarmupConfig.senderName` — the name warmup mail
   * renders. On update, `''` clears it (server maps to `null`). */
  senderName?: string;
  smtpHost: string;
  smtpPort: number;
  smtpEncryption: SmtpEncryption;
  imapHost: string;
  imapPort: number;
  imapEncryption: SmtpEncryption;
  smtpUsername: string;
  imapUsername?: string;
  password: string;
  /** A distinct IMAP password, only sent when the connect form's "Use a
   * different password for IMAP" toggle is on and non-blank. Omitted entirely
   * otherwise, in which case the server/probe fall back to `password` for the
   * IMAP leg too. */
  imapPassword?: string;
  /** The picked provider-catalog preset's `id` (e.g. `'zoho'`), used server-side
   * to validate the submitted email address matches the chosen ESP. Omitted for
   * Custom SMTP/Other (`preset === null`) — no restriction applies there. */
  espPresetId?: string;
};

/**
 * `POST /email-accounts/smtp/test` body. With `target`, only that channel's fields are
 * sent (see `buildTestPayload`); without it, the full shape probes both.
 */
export type TestSmtpPayload = {
  target?: 'smtp' | 'imap';
  smtpHost?: string;
  smtpPort?: number;
  smtpEncryption?: SmtpEncryption;
  smtpUsername?: string;
  password?: string;
  imapHost?: string;
  imapPort?: number;
  imapEncryption?: SmtpEncryption;
  imapUsername?: string;
  imapPassword?: string;
};

/**
 * Shape returned by `GET /email-accounts/:id/smtp` — the current SMTP/IMAP
 * server settings for an existing account, used to seed the edit form —
 * including the stored passwords, decrypted. That makes this response a
 * secret: `useSmtpConfigQuery` keeps it cached only while the form is mounted.
 * A stored password that can't be decrypted fails the whole request.
 */
export type SmtpConfig = {
  emailAddress: string;
  senderName: string | null;
  smtpHost: string;
  smtpPort: number;
  smtpEncryption: SmtpEncryption;
  smtpUsername: string;
  imapHost: string;
  imapPort: number;
  imapEncryption: SmtpEncryption;
  imapUsername: string | null;
  /** Whether the account has a distinct IMAP password stored (separate from
   * the SMTP password) — seeds the edit form's IMAP-password reveal-toggle. */
  hasDistinctImapPassword: boolean;
  smtpPassword: string;
  /** `null` when IMAP reuses the SMTP password. */
  imapPassword: string | null;
};

/**
 * Body for `PATCH /email-accounts/:id/smtp`. Same as a connect payload minus
 * the immutable `emailAddress`, with `password` optional — omit/empty to keep
 * the existing stored password.
 */
export type UpdateSmtpPayload = Omit<ConnectSmtpPayload, 'emailAddress' | 'password'> & {
  password?: string;
};

export type ProviderCategory =
  | 'MOST_POPULAR'
  | 'ADDITIONAL'
  | 'PARTNERS'
  | 'MARKETING';

export type ProviderEndpoint = {
  host: string;
  port: number;
  encryption: SmtpEncryption;
};

/** Alternate host names for a preset that still resolve back to it on the
 * backend — server-side match data only (see `resolvePresetByHost` in
 * `provider-presets.constants.ts`), not consumed by any client UI. SMTP/IMAP
 * Host are always plain editable inputs, regardless of this list. */
export type RegionHostOption = {
  id: string;
  smtpHost: string;
  imapHost: string;
};

/** A preset SMTP/IMAP provider config served by `GET /email-accounts/providers`. */
export type ProviderPreset = {
  id: string;
  name: string;
  category: ProviderCategory;
  logoUrl: string;
  credentials: { label: string; help: string };
  instructions: string;
  smtp: ProviderEndpoint;
  /** null for send-only relays with no IMAP inbox. */
  imap: ProviderEndpoint | null;
  /** Alternate host names that still resolve back to this preset on
   * reconnect/host-matching (backend-only concern) — not rendered as a
   * dropdown. Only the Zoho preset sets this today. */
  regionHostOptions?: RegionHostOption[];
};

/**
 * Bounded classification of a probe failure. Mirrors the API's
 * `ProbeFailureCause` enum (`apps/api/.../smtp/probe-failure-cause.enum.ts`).
 * Branch UI copy on this, not on `message`/`rawError`.
 */
export type ProbeFailureCause =
  | 'AUTH_FAILED'
  | 'HOST_UNREACHABLE'
  | 'TLS_FAILED'
  | 'TIMEOUT'
  | 'THROTTLED'
  | 'UNKNOWN';

/**
 * Probe outcome for one channel (SMTP or IMAP). Mirrors `SmtpChannelResultDto`.
 * `cause`, `message`, and `rawError` are present iff `isConnected` is false.
 * `message` is pre-rendered by the API — short, one line, user-safe — and is
 * meant to be rendered verbatim. `rawError` is the raw SMTP/IMAP server error
 * text; it's returned ONLY by `POST /email-accounts/smtp/test` (see
 * `testSmtpConnection` below) — the connect/update/reconnect endpoints never
 * include it. It's meant for a dedicated "Show error log" action (see
 * `ErrorLogDialog`/`SmtpTestErrorCard`), not a toast.
 */
export type SmtpChannelResult = {
  isConnected: boolean;
  cause?: ProbeFailureCause;
  message?: string;
  rawError?: string;
  /** This channel wasn't probed — the request's `target` asked for only the other
   * one. `isConnected` is meaningless here. */
  skipped?: boolean;
};

/** 200 body of `POST /email-accounts/smtp/test`. Mirrors `SmtpTestResultDto`. */
export type SmtpTestResult = {
  smtp: SmtpChannelResult;
  imap: SmtpChannelResult;
};

/* ──────────────────────────────────────────────────────────────────────────
 * DNS auth records — the four records as stored on `domainSecurityInfo`.
 *
 * Mirrors `SpfData`/`DkimData`/`DmarcData`/`MxRecordData` in `@trulyinbox/dns`,
 * which the API re-exports verbatim on `EmailAccountResponseDto`,
 * `EmailAccountListItemDto`, `PoolInfoResponseDto` and
 * `DomainSecurityResponseDto`. Declared once here and referenced from all of
 * `EmailAccount`, `EmailAccountListItem` and `DomainSecurity` — one wire shape,
 * one type.
 *
 * These REPLACE the four `spfValid`/`dkimValid`/`dmarcValid`/`mxValid` booleans.
 * The distinction the booleans could not express lives in the **nullability of
 * the record**, not in a field on it:
 *
 *   - `null`            → never successfully resolved. UNKNOWN, not missing.
 *                         The resolver blipped (SERVFAIL/timeout) or the domain
 *                         has never been swept, and the server deliberately
 *                         wrote nothing rather than stamping a false negative.
 *   - `{isValid:false}` → authoritative not-found (NXDOMAIN/ENODATA). The
 *                         domain genuinely does not publish this record.
 *   - `{isValid:true}`  → present, with the raw value alongside.
 *
 * `record ?? { isValid: false }` anywhere is the exact bug this shape exists to
 * prevent — it re-collapses "we don't know" into "it's broken" and takes healthy
 * accounts offline on resolver weather. Use `dnsRecordVerdict` in
 * `features/email-accounts/account-status.ts` to branch on all three.
 * ────────────────────────────────────────────────────────────────────────── */

/** SPF TXT record: validity plus the raw `v=spf1 …` string that was read. */
export type SpfData = { isValid: boolean; spfResults: string };

/** DMARC TXT record at `_dmarc.<domain>`: validity plus the raw policy string. */
export type DmarcData = { isValid: boolean; dmarcResults: string };

export type DkimData = {
  isValid: boolean;
  dkimRecord: string[];
  /** Which selector answered; null when none did. */
  selector: string | null;
};

export type MxRecord = { exchange: string; priority: number; ip?: string };

/** MX record set, lowest priority first. */
export type MxRecordData = { isValid: boolean; mxRecords: MxRecord[] };

export type EmailAccount = {
  id: number;
  emailAddress: string;
  /**
   * From `emailAccountWarmupConfig.senderName` — the account's single sender
   * name: what warmup content renders, and what the Warmup Settings page and the
   * Edit account (SMTP) modal both edit.
   */
  senderName: string | null;
  provider: string;
  authMethod: string;
  status: string;
  warmupStatus: string;
  // ── Canonical state machine (Phase 7) ──
  // `accountState` is the canonical 19-state value (e.g. `WARMUP_ACTIVE`,
  // `SUSPENDED_SPAM`, `WARMUP_PAUSED`, `DNS_FAILED`, `WARMUP_READY`).
  // `statusBucket` is the public bucket the API maps it to via
  // `ACCOUNT_STATE_BUCKET` — the source of truth for the status badge + filter
  // (see `account-status.ts`). `structuredStatus` is the latest transition's
  // self-describing status (null until a transition row exists).
  accountState: string;
  statusBucket: string;
  structuredStatus: StructuredStatus | null;
  /**
   * The mailbox send/receive check's two verdicts, judged independently.
   *
   * `sendCheckFailed` is already visible through `accountState` — the account is
   * suspended or in `MAILBOX_VERIFY_FAILED` — so nothing reads it for display.
   *
   * `receiveCheckFailed` is the one that needs its own field: such an account
   * stays `WARMUP_ACTIVE` and looks completely normal, while quietly receiving
   * no warmup mail at all. Without surfacing this the user just sees their
   * incoming numbers fall with no explanation anywhere in the product.
   *
   * Optional so an older API response (or a cached page from before the field
   * shipped) renders as "not flagged" rather than crashing on a missing key.
   */
  sendCheckFailed?: boolean;
  receiveCheckFailed?: boolean;
  // `warmupPausedAt` is the worker-committed pause signal. When the API
  // returns the optimistic snapshot from `pauseWarmup`, `warmupStatus` is
  // already `'PAUSED'` but `warmupPausedAt` stays null until the warmup-worker
  // commits the transition. `usePauseWarmupMutation` polls until this field
  // flips to a real timestamp (see `isPausePending` in `account-status.ts`).
  warmupPausedAt: string | null;
  // Structured reason for a non-running state, surfaced from the warmup-state
  // row. `warmupPausedReason` accompanies PAUSED, `throttleReason` accompanies
  // THROTTLED. Engine codes (e.g. `BOUNCE_RATE_6.25_PCT_NEW_DOMAIN`) —
  // humanize via `humanizeWarmupReason` in account-status.ts before display.
  warmupPausedReason: string | null;
  throttleReason: string | null;
  cooldownUntil: string | null;
  warmupMinLimit: number;
  warmupMaxLimit: number;
  warmupCurrentLimit: number;
  warmupRampupDay: number | null;
  warmupStrategy: string;
  timezone: string;
  poolTier: string;
  // `deliverabilityRate` is a percentage in the 0..100 range. The DB column is
  // decimal(5,2) and every writer in the platform (pool-calculator, threshold-
  // evaluator, ramp service, warmup seed) emits values in [0, 100]. The API
  // returns the entity column as-is, so consumers must NOT multiply by 100.
  // `deriveHealthStatus` and `deliverabilityScore` in account-status.ts treat
  // this as 0..100; the detail-page KpiStrip renders it directly.
  deliverabilityRate: number | null;
  inboxPlacementScore: number | null;
  // `emailAccountDomainScores.totalScore`, 0..100 — the exact column the
  // `setupScoreMin`/`setupScoreMax` filter matches on. `null` = never scored.
  setupScore: number | null;
  // See the DNS auth records block above — `null` is "never resolved", NOT a fail.
  //
  // Still on the detail payload (`GET /email-accounts/:id` returns them), but
  // NOTHING in the app reads them any more: every DNS derivation
  // (`requiredDnsRecords`, `deriveSetupScoreItems`) reads `domainSecurity`
  // below, so the list item and the detail account answer the same question the
  // same way. Kept declared because the API does send them.
  spfData: SpfData | null;
  dkimData: DkimData | null;
  dmarcData: DmarcData | null;
  mxRecordData: MxRecordData | null;
  dnsCheckedAt: string | null;
  /**
   * The account's domain-security snapshot.
   *
   * NOT part of `GET /email-accounts/:id` — the overview endpoint returns it as
   * a SIBLING of `account`, and `EmailAccountDetailPage` merges it on before
   * handing the account to anything shared with the table (`WarmupToggle`,
   * `warmupEnableBlockedReason`). Optional for exactly that reason: it is
   * present only once that merge has happened.
   *
   * Without it the shared warmup gate would read no DNS at all on the detail
   * page and disable the toggle on every account, since `dnsRecordVerdict`
   * treats an absent record as `unchecked` and the gate is fail-closed.
   */
  domainSecurity?: DomainSecurity | null;
  domainRegisteredAt: string | null;
  mailboxCreatedAt: string | null;
  lastVerifiedAt: string | null;
  pushNotificationsActive: boolean;
  createdAt: string;
  source: string | null;
  /**
   * The DWD workspace this account was bulk-imported from (domain-wide
   * delegation), null for every directly-connected account. The authoritative
   * "is this a DWD-managed mailbox" signal — `source` above is display-only
   * and was never reliably populated for DWD imports. Drives the DWD-aware
   * reconnect gating in `RowActionsMenu.tsx` / `DetailHeaderActions.tsx`.
   */
  dwdWorkspaceId: number | null;
  /**
   * The partner integration whose tokens manage this account, null for every
   * non-partner mailbox. Drives the warmup toggle's tooltip copy and the
   * Action Items page's fix action — nothing in this UI can reconnect a
   * partner-managed mailbox, so both surfaces point at the org owner instead
   * of offering a self-service Reconnect.
   */
  partnerIntegrationId: number | null;
  // ── Account-list table columns (TRU-307) ──
  /** When the account entered its warmup pool — the Warm-up Start Date column. */
  warmupStartDate: string | null;
  /** Configured target reply rate (%), not a measured one — the Reply Rate column. */
  replyRate: number;
  /** ESP-mode preference (`AUTO` | `CUSTOM`) — the Warm-up Preference column. */
  warmupPreference: string;
  contentIndustry: string | null;
  contentTopics: string[] | null;
  contentLanguage: string;
  /** Domain-blacklist signals; IP Blacklist has no data source yet (renders "—"). */
  blacklistedAt: string | null;
  blacklistSource: string | null;
  /** Sent/received tallies merged from ClickHouse per page (0 when no traffic). */
  todaySent: number;
  todayReceived: number;
  sevenDaySent: number;
  sevenDayReceived: number;
  /** Workspace tags assigned to this account (empty when none). */
  tags: Tag[];
  /** Set while this account is part of an in-flight async bulk job (enable/pause
   * warmup, delete, tag ops, warmup-settings update) — the id `use-active-bulk-
   * jobs-subscription.ts` subscribes to for live table patching + hard-refresh
   * reconnect. Absent when no bulk job is currently touching this account. */
  activeBulkJobId?: string;
  /** Mirrors `activeBulkJobId` for an in-flight CSV bulk-connect import. */
  activeImportId?: string;
};

/**
 * Slim row shape for `GET /email-accounts`'s `items[]` — the backend's
 * `EmailAccountListItemDto`. Also the shape the bulk-job/bulk-import/DWD-sync
 * `items[]` splice into the same list cache narrow to (see `BulkJobItem`,
 * `BulkImportItem` below, and `isSucceededSyncItem` in
 * `features/dwd-workspace/schemas.ts`).
 *
 * A deliberate hand-written mirror of `EmailAccount`'s field-by-field shape,
 * NOT `Omit<EmailAccount, ...>` — this is a distinct wire contract (a separate
 * backend DTO class), not a derived view of the same type. `Omit` would wire
 * this type to `EmailAccount` permanently, so the next detail-page-only field
 * added there (exactly what today's 8 dropped fields are) would silently gain
 * a slot here too, claiming the list endpoint returns a field the backend
 * never sends — a silent `undefined` at runtime with no type error. Mirrors
 * the same hand-maintained-local-copy pattern `StatusBucket` above already
 * uses for a backend union. Keep in lock-step with `EmailAccount` for every
 * field this type DOES carry; see docs/plans/email-accounts-list-slim-items.md
 * §4 for the field-by-field audit of what's kept vs. dropped and why.
 *
 * Omits (detail-page-only or unused in the frontend — see the plan's §4):
 * `cooldownUntil`, `domainRegisteredAt`, `mailboxCreatedAt`, `warmupStrategy`,
 * `timezone`, `poolTier`, `pushNotificationsActive`.
 *
 * `lastVerifiedAt` was ADDED BACK (TRU-1212) — it is not detail-page-only:
 * `inboxPlacementScore` is seeded at connect time with no
 * explicit value and so silently carries the DB column's classification-only
 * default of 100 until a real verification run overwrites it, so the score
 * alone can never distinguish "verified" from "never checked" — only
 * `lastVerifiedAt` (stamped in lockstep with a real score write) can.
 */
export type EmailAccountListItem = {
  id: number;
  emailAddress: string;
  /**
   * From `emailAccountWarmupConfig.senderName` — the account's single sender
   * name (what warmup mail carries). `deriveSetupScoreItems` scores the
   * "Sender name" row off this.
   */
  senderName: string | null;
  provider: string;
  authMethod: string;
  status: string;
  warmupStatus: string;
  accountState: string;
  statusBucket: string;
  structuredStatus: StructuredStatus | null;
  /**
   * The mailbox send/receive check's two verdicts, judged independently.
   *
   * `sendCheckFailed` is already visible through `accountState` — the account is
   * suspended or in `MAILBOX_VERIFY_FAILED` — so nothing reads it for display.
   *
   * `receiveCheckFailed` is the one that needs its own field: such an account
   * stays `WARMUP_ACTIVE` and looks completely normal, while quietly receiving
   * no warmup mail at all. Without surfacing this the user just sees their
   * incoming numbers fall with no explanation anywhere in the product.
   *
   * Optional so an older API response (or a cached page from before the field
   * shipped) renders as "not flagged" rather than crashing on a missing key.
   */
  sendCheckFailed?: boolean;
  receiveCheckFailed?: boolean;
  warmupPausedAt: string | null;
  warmupPausedReason: string | null;
  throttleReason: string | null;
  warmupMinLimit: number;
  warmupMaxLimit: number;
  warmupCurrentLimit: number;
  warmupRampupDay: number | null;
  // See the matching field on `EmailAccount` above — 0..100, never multiply by 100.
  deliverabilityRate: number | null;
  inboxPlacementScore: number | null;
  // See the matching field on `EmailAccount` above — render it, never re-derive it.
  setupScore: number | null;
  // NO top-level `spfData`/`dkimData`/`dmarcData`/`mxRecordData` here — unlike
  // `EmailAccount` above, which still carries them. The list item carries
  // `domainSecurity` (below), which holds the same four record objects PLUS the
  // per-field bands computed from them, so a second top-level copy is a second
  // thing to keep in sync — and every derivation that reads the wrong one
  // (`deriveSetupScoreItems`, `requiredDnsRecords`) silently disagrees with the
  // icons in the same row. Read them as `a.domainSecurity?.spfData`.
  dnsCheckedAt: string | null;
  // `null` means no verification
  // run has ever completed for this account, which is what actually gates
  // whether `inboxPlacementScore` is a real measurement or the connect-time
  // classification default.
  lastVerifiedAt: string | null;
  createdAt: string;
  source: string | null;
  /** See `EmailAccount.dwdWorkspaceId` above — same authoritative signal. */
  dwdWorkspaceId: number | null;
  /** See `EmailAccount.partnerIntegrationId` above — same authoritative signal. */
  partnerIntegrationId: number | null;
  warmupStartDate: string | null;
  replyRate: number;
  warmupPreference: string;
  contentIndustry: string | null;
  contentTopics: string[] | null;
  contentLanguage: string;
  blacklistedAt: string | null;
  blacklistSource: string | null;
  todaySent: number;
  todayReceived: number;
  sevenDaySent: number;
  sevenDayReceived: number;
  tags: Tag[];
  /**
   * Real SPF/DKIM/DMARC/MX/blacklist evidence, same shape as the overview
   * endpoint's field (see `DomainSecurity` below). Optional (not just
   * nullable) because the other two `items[]` producers that splice into this
   * same table cache — `BulkJobItem`/`BulkImportItem` — never set it at all,
   * so it's `undefined` on those rows, not `null`. Treat `undefined` exactly
   * like `null` ("not yet checked") everywhere this is read.
   */
  domainSecurity?: DomainSecurity | null;
  /**
   * Current-or-next sending window (UTC ISO) for the "Sending" column. `null` when
   * the account isn't sending or has no enabled slot; absent on bulk-job/import rows.
   */
  sendingWindow?: { startsAt: string; endsAt: string } | null;
  /**
   * The Reputation chart's newest point (last 30 days), which is what the
   * Outreach Readiness chip grades (see `deriveOutreachReadiness`). `null` = no
   * score row that recent, which reads Not Ready. Absent on bulk-job/import
   * rows, same as `domainSecurity`: treat `undefined` exactly like `null`.
   */
  latestWarmupScore?: LatestWarmupScore | null;
  activeBulkJobId?: string;
  activeImportId?: string;
};

/** One account's latest `emailAccountScoreDaily` row, as the accounts list sends it. */
export type LatestWarmupScore = {
  /** `YYYY-MM-DD`, the UTC day the row is for. */
  date: string;
  /** 0-100, the number the Reputation chart plots. */
  warmupScore: number;
};

export type DailySendStat = {
  date: string;
  sent: number;
  delivered: number;
  opened: number;
  replied: number;
};

export type LatestVerificationRun = {
  id: number;
  status: string;
  startedAt: string;
  completedAt: string | null;
  spfResult: string | null;
  dkimResult: string | null;
  dmarcResult: string | null;
  inboxCategoryResult: string | null;
};

/* ──────────────────────────────────────────────────────────────────────────
 * Domain security — the stored DNS/blacklist evidence + weighted score.
 *
 * Mirrors `DomainSecurityResponseDto` (apps/api). Served on the account
 * overview (a read of persisted evidence) and returned fresh by
 * `POST /email-accounts/:id/dns/check` (which re-resolves and re-persists
 * first). Both paths return the identical shape.
 *
 * The four record objects it carries are the SAME `SpfData`/`DkimData`/
 * `DmarcData`/`MxRecordData` declared above — the server serves
 * `domainSecurityInfo`'s columns verbatim on this payload and on the account
 * ones alike, so there is one type family, not a parallel per-payload one. What
 * this payload adds is `fields[]`: the server-owned band plus the
 * `indeterminate` flag, which is what the detail-page checklist renders.
 * ────────────────────────────────────────────────────────────────────────── */

/** Traffic-light band. The server owns this — never re-derive it from a score. */
export type DomainSecurityBand = 'green' | 'yellow' | 'red';

export type DomainSecurityFieldKey =
  | 'spf'
  | 'dkim'
  | 'dmarc'
  | 'mx'
  | 'domainBlacklist'
  | 'ipBlacklist';

/**
 * One row of the checklist.
 *
 * There is NO per-field band — the schema stores exactly one band, for the
 * total, and the API stopped inventing five more. Colour comes from
 * `recordTier` (see `detail/dns-records.ts`), which reads this pair plus the
 * blacklist breakdowns on the same payload. Don't re-derive a band here.
 */
export type DomainSecurityField = {
  key: DomainSecurityFieldKey;
  label: string;
  /** Points contributed, 0..maxScore. */
  score: number;
  /** Full weight: SPF 29, DKIM 18, domainBlacklist 18, DMARC 15, ipBlacklist 11, MX 9. */
  maxScore: number;
  /**
   * No usable evidence. Scores 0 exactly like a real failure, so this flag is
   * the only thing separating "couldn't check" from "missing".
   */
  indeterminate?: boolean;
};

/**
 * Per-zone DNSBL breakdown.
 *
 * The three buckets are the point: an empty `listed` with a non-empty `failed`
 * means UNKNOWN, not clean — and all three empty means the sweep never ran.
 * Rendering either as "0 listings" claims a check that didn't happen.
 */
export type BlacklistData = {
  ip: string | null;
  listed: string[];
  notListed: string[];
  failed: string[];
  isIpFromMx: boolean;
};

/* ──────────────────────────────────────────────────────────────────────────
 * Email validation — the mailbox send/receive test that runs beside the DNS and
 * blacklist checks (up to 3 test emails per direction: mailbox→seed proves
 * SENDING, seed→mailbox proves RECEIVING).
 *
 * The two vocabularies below mirror what the API SENDS, which is not quite what
 * it stores: the server projects a leg's stored `NOT_JUDGED` ("we could not
 * look" — no seed, every email void) AND a NULL (leg not tested by this run)
 * onto the single wire value `NOT_CHECKED`. Neither is a failure, and neither
 * may render as one — the same rule `DnsRecordVerdict`'s `unchecked` carries one
 * file over. The card reads these as enums, never as free text.
 * ────────────────────────────────────────────────────────────────────────── */

export type EmailValidationStatus = 'PASSED' | 'CHECKING' | 'FAILED' | 'NOT_CHECKED';

/** A leg never reports CHECKING — that lives on the parent `status`. */
export type EmailValidationLegResult = Exclude<EmailValidationStatus, 'CHECKING'>;

/** One direction of the test. `sent`/`arrived` are the real counts for the run. */
export type EmailValidationLeg = {
  result: EmailValidationLegResult;
  sent: number;
  arrived: number;
};

export type EmailValidation = {
  status: EmailValidationStatus;
  sending: EmailValidationLeg;
  receiving: EmailValidationLeg;
  /** ISO — last finished run/verdict time. */
  checkedAt: string | null;
  failureReason: string | null;
};

export type DomainSecurity = {
  domain: string;
  /**
   * 0..100 — a PLAIN SUM of the six field scores, read from
   * `emailAccountDomainScores.totalScore`. Not rescaled: a personal-domain
   * mailbox caps at 82 (no DKIM to publish) and one with no observed sending IP
   * loses a further 11. This is also the Setup Score.
   */
  score: number;
  band: DomainSecurityBand;
  fields: DomainSecurityField[];
  spfData: SpfData | null;
  dmarcData: DmarcData | null;
  dkimData: DkimData | null;
  mxRecordData: MxRecordData | null;
  domainBlacklistData: BlacklistData | null;
  ipBlacklistData: BlacklistData | null;
  ipAddress: string | null;
  recordsRefreshedAt: string | null;
  blacklistRefreshedAt: string | null;
  scoredAt: string | null;
  /**
   * From `emailAccountWarmupConfig.senderName` — the name that actually renders
   * the merge tokens in warmup content.
   */
  senderName: string | null;
  /**
   * The mailbox send/receive test result, or `null` when the feature is off
   * (`MAILBOX_VERIFICATION_ENABLED` false) — in which case the card renders NO
   * row for it, rather than an empty one.
   *
   * This field and the three below it are OPTIONAL, not merely nullable: the
   * API ships them in a later phase than this client, so until that deploy the
   * real payload carries no such key at all. Every reader already has to handle
   * the null case (feature off / never checked / allowed now), so `undefined`
   * lands on the same branch instead of on a crash.
   */
  emailValidation?: EmailValidation | null;
  /** ISO — the last check of ANY kind (connect, the daily health check, a
   * manual Re-check). This is the clock the 3h Re-check rule counts from, and
   * it is NOT `recordsRefreshedAt` (which moves only when DNS re-resolves). */
  lastCheckedAt?: string | null;
  /** ISO — when the next manual Re-check is allowed. `null`/absent = now. */
  nextManualCheckAt?: string | null;
  /** A test-email run is still out for this mailbox. */
  checkInProgress?: boolean;
};

export type EmailAccountOverview = {
  account: EmailAccount;
  /** Always 14 contiguous days ending today; zero-filled. */
  last14Days: DailySendStat[];
  /** 30 contiguous days ending today; zero-filled. `last14Days` is its tail.
   * Absent from an API build that predates it — hence optional. */
  last30Days?: DailySendStat[];
  latestVerificationRun: LatestVerificationRun | null;
  /** `null` = this domain has never been checked. NOT "clean". */
  domainSecurity: DomainSecurity | null;
  /**
   * The latest Mailbox History events, newest first (at most 3) — the same
   * items `GET :id/mailbox-history` returns. Absent from an API build that
   * predates it, hence optional.
   */
  recentHistory?: MailboxHistoryEvent[];
  /**
   * The latest emails this account sent that the recipient interacted with,
   * newest interaction first (at most 3, one per email). `null` = the read
   * failed, which is NOT "no engagement" (that is `[]`). Optional for the same
   * reason as `recentHistory`.
   */
  recentActivity?: OverviewRecentActivity[] | null;
  /**
   * The organization's IANA zone — render `recentHistory` / `recentActivity`
   * dates in it so they match the Activity tab. Optional for the same reason
   * as `recentHistory`; browser-local when absent.
   */
  timezone?: string;
};

/** Mirrors the API's `OVERVIEW_RECENT_ACTIVITY_EVENT_TYPE` — keep in lock-step. */
export type OverviewRecentActivityEventType =
  | 'forwarded'
  | 'moved_from_spam_to_inbox'
  | 'replied'
  | 'moved_from_other_to_inbox'
  | 'starred';

export type OverviewRecentActivity = {
  /** History id of the sent email (the thread root in the Activity tab). */
  historyId: number;
  /** A plain `string` on the wire so a newer API's unknown value is skipped, not a crash. */
  eventType: string;
  /** "YYYY-MM-DD HH:mm:ss", UTC. */
  eventAt: string;
  subject: string;
};

// Filter / sort union types are owned by the feature's Zod search-schema
// (`features/email-accounts/search-schema.ts`), where they're derived from the
// `as const` option arrays that power the URL enum. Re-export them here so the
// API-layer params stay in lock-step with the route's validated search without
// a second hand-maintained copy. (search-schema imports only `zod`, so this
// one-way re-export carries no circular-import risk.)
import type {
  EmailAccountStatusFilter,
  EmailAccountProviderFilter,
  EmailAccountStatusValue,
  EmailAccountProviderValue,
  EmailAccountSortField,
  EmailAccountSortDir,
  EmailAccountOutreachReadinessValue,
} from '@/features/email-accounts/search-schema';

export type {
  EmailAccountStatusFilter,
  EmailAccountProviderFilter,
  EmailAccountStatusValue,
  EmailAccountProviderValue,
  EmailAccountSortField,
  EmailAccountSortDir,
  EmailAccountOutreachReadinessValue,
};

/**
 * ESP-mode filter value (TRU-326). Maps to `cfg.espMode` (`AUTO`/`CUSTOM`) on
 * the backend. Mirrors the `esp` enum in the route's search-schema.
 */
export type EmailAccountEspFilter = 'auto' | 'custom';

export type ListEmailAccountsParams = {
  page?: number;
  limit?: number;
  /** Status filter (OR match within the dimension). One `status=` param per value. */
  status?: EmailAccountStatusValue[];
  /** Provider filter (OR match within the dimension). One `provider=` param per value. */
  provider?: EmailAccountProviderValue[];
  q?: string;
  sort?: EmailAccountSortField;
  dir?: EmailAccountSortDir;
  /** Tag-name filter (OR match, case-insensitive). One `tags=` param per name.
   * May also carry `WITHOUT_TAGS_FILTER_VALUE`. */
  tags?: string[];
  // ── Filter-modal params (TRU-326) ──────────────────────────────────────────
  /** ESP mode (`auto`/`custom`). */
  esp?: EmailAccountEspFilter;
  /** Warm-up max sending limit bounds (int 5–100). */
  maxLimitMin?: number;
  maxLimitMax?: number;
  /** Email-domain filter — exact match, case-insensitive (not a substring match). */
  domain?: string;
  /** Warm-up start-date range (ISO `YYYY-MM-DD`). */
  startDateFrom?: string;
  startDateTo?: string;
  /** Deliverability-rate bounds (int 0–100). */
  deliverabilityMin?: number;
  deliverabilityMax?: number;
  /** Outreach Readiness filter (OR match within the dimension), V1.1. One
   * `outreachReadiness=` param per value. */
  outreachReadiness?: EmailAccountOutreachReadinessValue[];
  /** Setup Score bounds (int 0–100), V1.1. */
  setupScoreMin?: number;
  setupScoreMax?: number;
  /** Per-ESP percentage bounds (int 0–100). Each pair applies independently. */
  espGoogleMin?: number;
  espGoogleMax?: number;
  espMicrosoftMin?: number;
  espMicrosoftMax?: number;
  espOthersMin?: number;
  espOthersMax?: number;
  /** Only accounts that will send today: in a sending state (active,
   * throttled, maintenance — never paused, suspended, disconnected or in
   * setup) with an enabled warm-up slot on today's weekday, in the account's
   * own timezone. A slot already run today still counts. */
  scheduledToday?: boolean;
};

// Per-bucket totals for the toolbar pills + empty-state gating. Keys are the
// canonical state buckets (Phase 7) — `all` plus the seven public buckets the
// `?status=` filter exposes (the other `setup` connection states are not a
// filter option, so they don't get a count key). Server-computed with the
// same workspace/provider/q filters but ignoring `status`.
export type EmailAccountStatusCounts = {
  all: number;
  active: number;
  throttled: number;
  paused: number;
  suspended: number;
  cooldown: number;
  maintenance: number;
  disconnected: number;
};

export type PaginatedEmailAccounts = {
  items: EmailAccountListItem[];
  total: number;
  page: number;
  limit: number;
  counts: EmailAccountStatusCounts;
};

/**
 * Fetch a paginated, filtered slice of the workspace's email accounts.
 *
 * The backend now does the filtering/counting server-side and returns a
 * paginated envelope. `status`/`provider` are multi-select: each selected value
 * is appended as a repeated param (OR-matched within the dimension); an empty
 * set sends nothing. `q` is trimmed and skipped when empty.
 *
 * `counts` is computed by the server with the same workspace/provider/q
 * filters but ignoring `status`, so the per-bucket pill totals stay stable
 * regardless of which status filter is active.
 */
export async function listAccounts(
  params: ListEmailAccountsParams = {},
): Promise<PaginatedEmailAccounts> {
  const sp = new URLSearchParams();
  if (params.page !== undefined) sp.set('page', String(params.page));
  if (params.limit !== undefined) sp.set('limit', String(params.limit));
  // Repeated `status=`/`provider=` params — the backend OR-matches them within
  // each dimension. The `'all'` sentinel is gone: an empty array sends nothing.
  for (const status of params.status ?? []) sp.append('status', status);
  for (const provider of params.provider ?? []) sp.append('provider', provider);
  if (params.q && params.q.trim().length > 0) sp.set('q', params.q.trim());
  // Map the URL-facing `sort`/`dir` onto the API's `sortBy`/`sortDir`. Omit the
  // backend defaults (createdAt/desc) so a default-sorted URL stays clean and
  // its cache key matches the loader's default-primed entry.
  if (params.sort && params.sort !== 'createdAt') sp.set('sortBy', params.sort);
  if (params.dir && params.dir !== 'desc') sp.set('sortDir', params.dir);
  // Repeated `tags=` params — the backend OR-matches them case-insensitively.
  for (const tag of params.tags ?? []) {
    if (tag.trim().length > 0) sp.append('tags', tag);
  }
  // ── Filter-modal params (TRU-326) — each appended only when active so an
  // inactive filter is OMITTED from the request entirely. ──
  if (params.esp) sp.set('esp', params.esp);
  if (params.maxLimitMin !== undefined) sp.set('maxLimitMin', String(params.maxLimitMin));
  if (params.maxLimitMax !== undefined) sp.set('maxLimitMax', String(params.maxLimitMax));
  if (params.domain && params.domain.trim().length > 0) sp.set('domain', params.domain.trim());
  if (params.startDateFrom) sp.set('startDateFrom', params.startDateFrom);
  if (params.startDateTo) sp.set('startDateTo', params.startDateTo);
  if (params.deliverabilityMin !== undefined)
    sp.set('deliverabilityMin', String(params.deliverabilityMin));
  if (params.deliverabilityMax !== undefined)
    sp.set('deliverabilityMax', String(params.deliverabilityMax));
  // Repeated `outreachReadiness=` params — OR-matched within the dimension,
  // same shape as `status`/`provider` above.
  for (const value of params.outreachReadiness ?? []) sp.append('outreachReadiness', value);
  if (params.setupScoreMin !== undefined) sp.set('setupScoreMin', String(params.setupScoreMin));
  if (params.setupScoreMax !== undefined) sp.set('setupScoreMax', String(params.setupScoreMax));
  if (params.espGoogleMin !== undefined) sp.set('espGoogleMin', String(params.espGoogleMin));
  if (params.espGoogleMax !== undefined) sp.set('espGoogleMax', String(params.espGoogleMax));
  if (params.espMicrosoftMin !== undefined)
    sp.set('espMicrosoftMin', String(params.espMicrosoftMin));
  if (params.espMicrosoftMax !== undefined)
    sp.set('espMicrosoftMax', String(params.espMicrosoftMax));
  if (params.espOthersMin !== undefined) sp.set('espOthersMin', String(params.espOthersMin));
  if (params.espOthersMax !== undefined) sp.set('espOthersMax', String(params.espOthersMax));
  if (params.scheduledToday) sp.set('scheduledToday', 'true');
  const qs = sp.toString();
  return apiFetch<PaginatedEmailAccounts>(
    `/email-accounts${qs ? `?${qs}` : ''}`,
  );
}

/** One distinct email domain in the workspace plus how many accounts use it
 * (`gmail.com (10)`). Backs the Domain filter dropdown. */
export type EmailAccountDomain = {
  domain: string;
  count: number;
};

/**
 * Fetch every distinct domain in the workspace with its per-domain account
 * count (`GET /email-accounts/domains`), alphabetical, lower-cased. Not
 * filtered by any other query param — always the full workspace picture
 * (backs the Domain filter dropdown, which is deliberately not scoped to the
 * drawer's other in-flight filters).
 */
export async function listEmailAccountDomains(): Promise<EmailAccountDomain[]> {
  return apiFetch<EmailAccountDomain[]>('/email-accounts/domains');
}

/** One row of the mailbox-switcher list (TRU-1250). Four fields, deliberately
 * — see `lookupEmailAccounts` for why this is not `EmailAccountListItem`. */
export type EmailAccountLookupItem = {
  id: number;
  emailAddress: string;
  provider: string;
  /** Raw state, not the derived bucket. `accountStateBucket()`
   * (`features/email-accounts/account-status.ts`) maps it to a tone. */
  accountState: string;
};

export type EmailAccountLookupPage = {
  items: EmailAccountLookupItem[];
  /** Server-computed — never infer from `items.length === limit`, which is
   * wrong on an exact-boundary page. */
  hasMore: boolean;
};

export type LookupEmailAccountsParams = {
  /** PREFIX match — `acme` matches `acme@…`, not `john@acme.com`. */
  q?: string;
  limit?: number;
  offset?: number;
};

/**
 * The mailbox-switcher list — prefix-searchable, paged. NOT `listAccounts` with
 * a smaller `limit`: that response carries a 3-join aggregate over every
 * workspace account, and its counts cache is keyed on `q` so a search box
 * misses it every keystroke. See the plan doc §3.
 */
export async function lookupEmailAccounts(
  params: LookupEmailAccountsParams = {},
): Promise<EmailAccountLookupPage> {
  const sp = new URLSearchParams();
  if (params.q && params.q.trim().length > 0) sp.set('q', params.q.trim());
  if (params.limit !== undefined) sp.set('limit', String(params.limit));
  if (params.offset) sp.set('offset', String(params.offset));
  const qs = sp.toString();
  return apiFetch<EmailAccountLookupPage>(
    `/email-accounts/lookup${qs ? `?${qs}` : ''}`,
  );
}

export async function getEmailAccount(id: number): Promise<EmailAccount> {
  return apiFetch<EmailAccount>(`/email-accounts/${id}`);
}

/**
 * Sends the browser's zone so the daily series is bucketed in the same local
 * days as the Analytics tab (which sends the same `resolvedTimeZone()`).
 */
export async function getEmailAccountOverview(id: number): Promise<EmailAccountOverview> {
  const sp = new URLSearchParams({ timeZone: resolvedTimeZone() });
  return apiFetch<EmailAccountOverview>(`/email-accounts/${id}/overview?${sp.toString()}`);
}

/**
 * One day of the account's reputation snapshot. Both scores are a ROLLING
 * WINDOW ending on `date` — 15 days for `warmupScore`, 7 for
 * `deliverabilityScore` — not that day's own figure, so consecutive points
 * share most of their input and the series is smooth enough to plot directly.
 *
 * Both are whole numbers 0-100, rounded up server-side.
 *
 * Corollary: never sum or average these across days. Each point is already the
 * finished answer for its date, and adjacent points overlap.
 *
 * Bounce and spam rates are deliberately NOT here: they are no longer stored
 * per day (the stored figures counted soft bounces only and disagreed with what
 * actually throttles an account) and now arrive once per response as
 * `AccountScoreRates`.
 */
export type AccountScoreDay = {
  date: string;
  warmupScore: number;
  deliverabilityScore: number;
};

/**
 * The account's bounce and spam rates over a trailing 7-day window, computed
 * live at request time rather than read from the daily snapshot table. Whole
 * numbers 0-100, rounded up server-side.
 *
 * Zeroes here are a REAL measurement — the account is clean, or sent nothing.
 * "Could not measure" is the whole object being `null`, never a zero on it; see
 * `getEmailAccountScores`.
 */
export type AccountScoreRates = {
  bouncePercentage: number;
  spamPercentage: number;
};

/**
 * Server-computed reputation series for the "Warmup performance" chart
 * (`GET /email-accounts/:id/scores`), oldest first, plus the account's live
 * trailing-7-day bounce/spam rates.
 *
 * `days` may hold FEWER days than requested — the snapshot cron writes one row
 * per account per day, so a recently connected account simply has less history.
 * The chart plots what it gets; zero-filling would render a fabricated 0% as a
 * catastrophic reputation drop.
 *
 * `rates` carries the same distinction one level up, and it must survive all
 * the way to the render:
 * - `null` → the rates could not be read at all (the analytics store was
 *   unreachable). Render `—`. Rendering `0%` here would report a reassuring
 *   "clean" for a lookup that never resolved.
 * - `{ bouncePercentage: 0, spamPercentage: 0 }` → measured, and genuinely
 *   zero. Render `0%`.
 */
export async function getEmailAccountScores(
  id: number,
  days = 30,
): Promise<{ days: AccountScoreDay[]; rates: AccountScoreRates | null }> {
  return apiFetch<{ days: AccountScoreDay[]; rates: AccountScoreRates | null }>(
    `/email-accounts/${id}/scores?days=${days}`,
  );
}

/**
 * Re-resolves SPF/DKIM/DMARC/MX, re-persists the evidence, and returns the
 * refreshed snapshot. POST, not GET, because it writes.
 *
 * Returns `null` only when the account's domain has no stored evidence even
 * after the refresh (every lookup failed on a domain never swept before).
 */
export async function checkDns(id: number): Promise<DomainSecurity | null> {
  return apiFetch<DomainSecurity | null>(`/email-accounts/${id}/dns/check`, { method: 'POST' });
}

/** Static catalog of preset SMTP/IMAP provider configurations (TRU-52). */
export async function getProviderPresets(): Promise<ProviderPreset[]> {
  return apiFetch<ProviderPreset[]>('/email-accounts/providers');
}

export async function testSmtpConnection(payload: TestSmtpPayload): Promise<SmtpTestResult> {
  return apiFetch<SmtpTestResult>('/email-accounts/smtp/test', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function connectSmtp(payload: ConnectSmtpPayload): Promise<EmailAccount> {
  return apiFetch<EmailAccount>('/email-accounts/smtp/connect', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/** Fetch an existing account's SMTP/IMAP server settings (and stored
 * passwords) to seed the edit form. */
export async function getSmtpConfig(id: number): Promise<SmtpConfig> {
  return apiFetch<SmtpConfig>(`/email-accounts/${id}/smtp`);
}

/** `GET /email-accounts/:id/smtp` failed because a stored password can't be
 * decrypted (`023001`). Deterministic — retrying never helps; the remedy is to
 * re-enter the password through `updateSmtp`. */
export function isStoredPasswordUnreadable(err: unknown): err is ApiError {
  return (
    err instanceof ApiError &&
    err.errorCode === ErrorCode.EmailAccounts.GetSmtpConfig.StoredPasswordUnreadable
  );
}

/** Update an existing account's SMTP/IMAP settings. Omitting `password` (or
 * sending an empty string) keeps the stored password. Returns the updated
 * account in the same shape as `connectSmtp`. */
export async function updateSmtp(
  id: number,
  payload: UpdateSmtpPayload,
): Promise<EmailAccount> {
  return apiFetch<EmailAccount>(`/email-accounts/${id}/smtp`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

export async function enableWarmup(id: number): Promise<EmailAccount> {
  return apiFetch<EmailAccount>(`/email-accounts/${id}/warmup/enable`, { method: 'POST' });
}

export async function pauseWarmup(id: number): Promise<EmailAccount> {
  return apiFetch<EmailAccount>(`/email-accounts/${id}/warmup/pause`, { method: 'POST' });
}

/**
 * Resume warmup for a paused or suspended account (Phase 7). Throws `ApiError`
 * w/ code `ResumeWarmup.NotSuspended` (`022601`) when the account is neither
 * paused nor suspended. Returns the updated account, same shape as `pauseWarmup`.
 */
export async function resumeWarmup(id: number): Promise<EmailAccount> {
  return apiFetch<EmailAccount>(`/email-accounts/${id}/warmup/resume`, { method: 'POST' });
}

export async function disconnectAccount(id: number): Promise<EmailAccount> {
  return apiFetch<EmailAccount>(`/email-accounts/${id}/disconnect`, { method: 'POST' });
}

/** Body for `POST /email-accounts/:id/reconnect`. Every field is optional at
 * the wire level — an OAuth account's reconnect sends an empty body (server
 * returns `{ redirectUrl }`); a PASSWORD account's reconnect sends the full
 * SMTP/IMAP/password set (server 400s `Reconnect.MissingSmtpFields` if any
 * of host/port/encryption/username/password are missing). */
export type ReconnectSmtpPayload = Partial<Omit<ConnectSmtpPayload, 'emailAddress'>>;

/** Reconnect a disconnected account. OAuth accounts: call with no payload
 * (or `{}`) — returns `{ redirectUrl }` to navigate the browser to. PASSWORD
 * accounts: call with the full SMTP/IMAP/password payload — returns the
 * updated `EmailAccount`, same shape `connectSmtp`/`updateSmtp` return. */
export async function reconnectAccount(
  id: number,
  payload: ReconnectSmtpPayload = {},
): Promise<EmailAccount | { redirectUrl: string }> {
  return apiFetch<EmailAccount | { redirectUrl: string }>(`/email-accounts/${id}/reconnect`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export type WarmupScheduleItem = {
  id: number;
  dayOfWeek: number;
  /** 0..2 — slot position within the day; rows ordered by (dayOfWeek, slotIndex). */
  slotIndex: number;
  /** 1..100 — this slot's share of the day's volume; enabled-day slots sum to 100. */
  weight: number;
  enabled: boolean;
  startTime: string;
  endTime: string;
};

export type WarmupSettings = {
  /** Daily send floor — fixed at `WARMUP_MIN_LIMIT` (5) for every mailbox:
   * where warmup starts and where a throttle exits. Not user-editable. */
  warmupMinLimit: number;
  /** The user's max daily sends — the ceiling the ramp climbs toward. Editable
   * from `WARMUP_MIN_LIMIT` up to `maxWarmupPerAccount` (PATCH `warmupMaxLimit`). */
  warmupMaxLimit: number;
  /** Daily sending limit in force today (read-only). The ramp moves it between
   * `warmupMinLimit` and `warmupMaxLimit`; the auto reply/thread bands follow it. */
  warmupCurrentLimit: number;
  /** Plan cap on `warmupMaxLimit` (a per-org override wins over the plan's
   * value), or `null` when the plan is unlimited — never coerced to a number. A
   * higher max is rejected with `022303` (`UpdateWarmupSettings.ExceedsPlanLimit`). */
  maxWarmupPerAccount: number | null;
  /** Plan cap on reply rate (%), or `null` when the plan is uncapped — the
   * ceiling the 40+/day auto band stretches to, and (via
   * `?? REPLY_RATE_ABSOLUTE_CAP`) the custom-reply slider's ceiling. Shipped raw:
   * null and 65 resolve to different auto bands at 40+/day. */
  replyRateCap: number | null;
  dkimSelector: string | null;
  senderName: string | null;
  warmupFolder: string | null;
  /** Thread-tendency band in force, derived from `warmupCurrentLimit`. Read-only. */
  threadTendencyMin: number;
  threadTendencyMax: number;
  espMode: string;
  espGooglePct: number | null;
  espMicrosoftPct: number | null;
  espOthersPct: number | null;
  // ── Per-section plan gates ──────────────────────────────────────────────
  // Resolved server-side against the org's plan and returned with the settings
  // themselves, so the page never needs a second `GET /billing/capabilities`.
  // Each mirrors one write-time gate the API enforces; a denied write throws the
  // matching `ErrorCode.EmailAccounts.UpdateWarmupSettings.*RequiresUpgrade`.
  /** `espMode: 'CUSTOM'` (`AUTO` is ungated). */
  customEspAllowed: boolean;
  /** `contentMode: 'industry_topic'`/`'custom_prompt'` + `industry`/`topics`/
   * `customPrompt` (`contentMode: 'default'` is ungated). */
  customContentAllowed: boolean;
  /** `templateCollectionId` + `templateRatioPercent` (clearing to `null` is ungated). */
  templatesAllowed: boolean;
  /** The weekly `schedule[]` editor. `timezone` is NOT gated — it belongs to the
   * always-open General group even though it renders in the schedule section. */
  customScheduleAllowed: boolean;
  /** The warmup content `language`. */
  customLanguageAllowed: boolean;
  /** `replyRateMode` + `replyRate`. Locked on `free` only. NOT the same thing as
   * `replyRateCap` below: this says whether the section may be touched at all,
   * that says how high the value may go once it may. A null `replyRateCap` on an
   * unlocked plan means "no ceiling", never "no access". */
  replyRateAllowed: boolean;
  planName: string | null;
  language: string;
  contentMode: ContentMode;
  industry: string | null;
  topics: string[] | null;
  customPrompt: string | null;
  timezone: string;
  warmupStrategy: string;
  schedule: WarmupScheduleItem[];
  /** Assigned template collection id, or null (100% AI content). */
  templateCollectionId: number | null;
  /** Per-send chance (%) of drawing from templateCollectionId instead of AI-generated content. */
  templateRatioPercent: number;
};

/**
 * How a warmup account's send content is generated — mirrors the
 * `UpdateWarmupSettingsDto.contentMode` enum on the API. Declared here (the
 * resource module) as the single source of truth; the form layer imports it.
 */
export type ContentMode = 'default' | 'industry_topic' | 'custom_prompt';

export type UpdateWarmupSettingsPayload = {
  /** The user's max daily sends (int ≥ `WARMUP_MIN_LIMIT`). Above the plan's
   * `maxWarmupPerAccount` the server rejects with `022303`. */
  warmupMaxLimit?: number;
  timezone?: string;
  dkimSelector?: string;
  senderName?: string;
  warmupFolder?: string;
  /** How the reply band is set: `CUSTOM` uses the `replyRate` value below; `LEVEL`
   * is "Auto" — the band follows the mailbox's current daily limit (the wire
   * value keeps its old name). Omit to leave unchanged. */
  replyRateMode?: 'LEVEL' | 'CUSTOM';
  /** The custom reply-rate value (int 0–65). Required by the backend when
   * `replyRateMode === 'CUSTOM'`; ignored otherwise. */
  replyRate?: number;
  espMode?: 'AUTO' | 'CUSTOM';
  espGooglePct?: number;
  espMicrosoftPct?: number;
  espOthersPct?: number;
  language?: string;
  contentMode?: ContentMode;
  industry?: string;
  topics?: string[];
  customPrompt?: string;
  schedule?: Array<{
    dayOfWeek: number;
    /** 0..2, optional — defaults to 0 server-side (legacy single-slot body). */
    slotIndex?: number;
    /** 1..100 integer, optional — defaults to 100 server-side. */
    weight?: number;
    enabled: boolean;
    startTime: string;
    endTime: string;
  }>;
  /** Template collection to draw send content from. `null` clears the
   * assignment (100% AI). Omit to leave unchanged. */
  templateCollectionId?: number | null;
  /** Per-send chance (%, 0-100) of drawing from templateCollectionId instead
   * of AI-generated content. */
  templateRatioPercent?: number;
};

export async function deleteEmailAccount(id: number): Promise<void> {
  await apiFetch<void>(`/email-accounts/${id}`, { method: 'DELETE' });
}

// ── Async bulk-job framework ──────────────────────────────────────────────────
// Every bulk operation is enqueued as a single async job:
//   POST /email-accounts/bulk/jobs   → { jobId, total }
//   GET  /email-accounts/bulk/jobs/:jobId → BulkJobStatus (poll until terminal)
// The selection is sent as an envelope — either an explicit `ids` list or a
// `filter` mirroring the list query's URL filters (with per-row `excludeIds`)
// so a "select all 10,000 matching" never has to enumerate ids client-side.
// Per-account `failures[].error` is a 6-digit catalog code string; the panel
// maps it to a label via `bulk-job-failure-labels.ts`.

export type BulkOperation =
  | 'enable-warmup'
  | 'pause-warmup'
  | 'delete'
  | 'add-tags'
  | 'remove-tags'
  | 'update-warmup-settings';

export type BulkSelectionFilter = {
  status?: EmailAccountStatusValue[];
  provider?: EmailAccountProviderValue[];
  q?: string;
  /** May also carry `WITHOUT_TAGS_FILTER_VALUE`. */
  tags?: string[];
  // ── Filter-modal params (TRU-326) — mirror the list query so a filter-mode
  // bulk op resolves against the exact set the table shows. ──
  esp?: EmailAccountEspFilter;
  maxLimitMin?: number;
  maxLimitMax?: number;
  domain?: string;
  startDateFrom?: string;
  startDateTo?: string;
  deliverabilityMin?: number;
  deliverabilityMax?: number;
  /** Only accounts that will send today — see `ListEmailAccountsParams.scheduledToday`. */
  scheduledToday?: boolean;
  // Outreach Readiness, Setup Score and the per-ESP % ranges — the list filters
  // on these, so a "select all matching" that dropped them acted on more
  // mailboxes than the table showed.
  outreachReadiness?: EmailAccountOutreachReadinessValue[];
  setupScoreMin?: number;
  setupScoreMax?: number;
  espGoogleMin?: number;
  espGoogleMax?: number;
  espMicrosoftMin?: number;
  espMicrosoftMax?: number;
  espOthersMin?: number;
  espOthersMax?: number;
};

export type BulkSelection =
  | { mode: 'ids'; ids: number[] }
  | { mode: 'filter'; filter: BulkSelectionFilter; excludeIds?: number[] };

export type StartBulkOperationBody = {
  operation: BulkOperation;
  selection: BulkSelection;
  params?: {
    addTags?: string[];
    removeTags?: string[];
    /** Sparse warmup-settings patch for `operation: 'update-warmup-settings'`.
     * Only the keys the user changed are present; untouched keys keep each
     * account's own config (a per-account sparse merge). */
    warmupSettings?: UpdateWarmupSettingsPayload;
  };
};

export type BulkJobResponse = { jobId: string; total: number };

/** A single per-account failure. `error` is a 6-digit catalog code string. */
export type BulkJobFailure = { id: number; error: string };

/**
 * A single account row embedded in a bulk job's `items[]` — the slim
 * `EmailAccountListItem` row shape plus this job's per-item outcome (mirrors
 * the backend narrowing every `items[]` producer to the same 31-field shape —
 * see docs/plans/email-accounts-list-slim-items.md §6a). `jobItemStatus`/
 * `jobItemError` are distinct fields (not `status`/`error`) so they don't
 * shadow `EmailAccountListItem`'s own `status` field.
 */
export type BulkJobItem = EmailAccountListItem & {
  jobItemStatus: 'pending' | 'succeeded' | 'failed';
  jobItemError?: string;
};

export type BulkJobStatus = {
  jobId: string;
  operation: BulkOperation | string;
  status: 'pending' | 'running' | 'complete' | 'failed';
  total: number;
  processed: number;
  succeeded: number;
  failed: number;
  failures: BulkJobFailure[];
  items: BulkJobItem[];
};

/** Enqueue an async bulk operation. Returns the job id + the resolved total. */
export async function startBulkOperation(
  body: StartBulkOperationBody,
): Promise<BulkJobResponse> {
  return apiFetch<BulkJobResponse>('/email-accounts/bulk/jobs', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * One-shot seed fetch for a bulk job's status. Called exactly once by
 * `useBulkJobStatusQuery` when `jobId` first exists; live updates after that
 * come from Firestore, not further calls to this. Throws `ApiError` w/ code
 * `023204` if expired.
 */
export async function getBulkJobStatus(jobId: string): Promise<BulkJobStatus> {
  return apiFetch<BulkJobStatus>(`/email-accounts/bulk/jobs/${jobId}`);
}

// ── Bulk CSV connect ─────────────────────────────────────────────────────────
// `POST /email-accounts/bulk-connect` takes a multipart CSV (max 10,000 rows, 5 MB),
// validates + queues the valid rows, and returns an `importId` plus the rows it
// rejected synchronously. The per-row *connection* outcome is resolved by a
// background worker; `useBulkImportStatusQuery` seeds once via
// `getBulkImportStatus(importId)` then reads live updates from Firestore
// until `status` flips to `complete`.

/** A CSV row rejected before queueing (bad header/port/encryption/duplicate). */
export type BulkConnectInvalidRow = {
  /** 1-based CSV row number (header = 1, first data row = 2). */
  row: number;
  emailAddress?: string;
  error: string;
};

export type BulkConnectResult = {
  importId: string;
  queued: number;
  invalid: BulkConnectInvalidRow[];
};

export type BulkImportItemStatus = 'pending' | 'connected' | 'failed';

/**
 * A single account row embedded in a bulk import's `items[]` — the slim
 * `EmailAccountListItem` row shape plus this import's per-row connection
 * outcome (see `BulkJobItem` above for the same narrowing rationale).
 * `connectionStatus`/`connectionError` are distinct fields (not `status`/
 * `error`) so they don't shadow `EmailAccountListItem`'s own `status` field.
 */
export type BulkImportItem = EmailAccountListItem & {
  connectionStatus: BulkImportItemStatus;
  connectionError?: string;
};

export type BulkImportStatus = {
  importId: string;
  status: 'processing' | 'complete';
  total: number;
  connected: number;
  failed: number;
  pending: number;
  invalid: BulkConnectInvalidRow[];
  items: BulkImportItem[];
};

/**
 * Upload a CSV to bulk-connect SMTP/IMAP accounts. `apiFetch` can't send
 * multipart, so we hand-roll `fetch` with `FormData` (field name `file`) using
 * the same bearer token, then unwrap the standard `{ success, data }` envelope
 * exactly like `apiFetch` does. (No 401 refresh-retry here — matches the
 * existing `uploadAvatar` multipart path.)
 */
export async function uploadBulkCsv(file: File): Promise<BulkConnectResult> {
  if (typeof window === 'undefined') {
    throw new ApiError(500, 'uploadBulkCsv must run in the browser');
  }
  const token = tokenStorage.get('access_token');
  const formData = new FormData();
  formData.append('file', file);

  const res = await fetch(`${env.apiUrl}/email-accounts/bulk-connect`, {
    method: 'POST',
    body: formData,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  const envelope = (await res.json().catch(() => null)) as
    | ApiEnvelope<BulkConnectResult>
    | null;
  if (envelope && typeof envelope === 'object' && 'success' in envelope) {
    if (envelope.success) return envelope.data;
    throw new ApiError(res.status, envelope.errorMessage, envelope.errorCode, envelope);
  }
  throw new ApiError(res.status, 'Bulk upload failed');
}

/**
 * One-shot seed fetch for a bulk import's per-row results. Called exactly
 * once by `useBulkImportStatusQuery` when `importId` first exists; live
 * updates after that come from Firestore.
 */
export async function getBulkImportStatus(importId: string): Promise<BulkImportStatus> {
  return apiFetch<BulkImportStatus>(`/email-accounts/bulk-connect/${importId}`);
}

// ── Tags ──────────────────────────────────────────────────────────────────────
// Workspace-scoped tag catalog + account assignment. The assign/bulk endpoints
// take tag *names* (not ids) as the full desired set — the backend create-on-
// assign, diffs, and orphan-cleans removed tags server-side.

/** Workspace tag catalog with usage counts (`GET /tags`). */
export async function listTags(): Promise<WorkspaceTag[]> {
  return apiFetch<WorkspaceTag[]>('/tags');
}

/**
 * Replace an account's full tag set (`PUT /email-accounts/:id/tags`). `names`
 * is the complete desired set (≤ 25); an empty array clears all tags. Returns
 * the updated account with its resolved `tags: Tag[]`.
 */
export async function setAccountTags(id: number, names: string[]): Promise<EmailAccount> {
  return apiFetch<EmailAccount>(`/email-accounts/${id}/tags`, {
    method: 'PUT',
    body: JSON.stringify({ tags: names }),
  });
}

export async function getWarmupSettings(id: number): Promise<WarmupSettings> {
  return apiFetch<WarmupSettings>(`/email-accounts/${id}/warmup-settings`);
}

export async function updateWarmupSettings(
  id: number,
  payload: UpdateWarmupSettingsPayload,
): Promise<WarmupSettings> {
  return apiFetch<WarmupSettings>(`/email-accounts/${id}/warmup-settings`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

// ── Email Account Activity tab (v2) ──────────────────────────────────────────
// Three endpoints, not one combined feed: `GET /:id/activity` (day summary,
// counts only, fixed 5-day window), `GET /:id/activity/cycles` (one day's
// THREADS — one feed item per `parentId` conversation, spanning both
// directions, lazy-loaded per day, ordered and cursor-paginated on each
// thread's anchor time `startedAt` via `lastEventAt`/`lastHistoryId`),
// `GET /:id/mailbox-history` (bounded account-lifecycle list). Mirrors the
// backend's `ACTIVITY_EVENT_TYPE`, `ACTIVITY_MESSAGE_KIND` and
// `ACTIVITY_CYCLE_DIRECTION` constants
// (`apps/api/.../constants/activity.constants.ts`) — keep in lock-step, same
// rationale as `StatusBucket` above (this package isn't a dependency of
// `apps/web`, so it can't be imported directly).
//
// There is no `direction` query param: a thread spans both sides by
// definition, so the former Warmup Sent / Received pill partitions nothing.
// `engagedOnly` (default `true` server-side) replaces it and hides threads
// nobody engaged with.
//
// Masking is SERVER-SIDE in v2 — every address that is not this account's own
// arrives already masked (e.g. "3k********t+3@gmail.com"), including BOTH
// ends of an `observed` message. Never mask it again client-side
// (double-masking corrupts the display string); there is no client-side
// `mask-email.ts` in v2.
export type ActivityEventType =
  | 'sent'
  /** A confirmed arrival in this mailbox (received cycles only). Headlines only when
   * nothing more important (landed, opened, …) has happened yet. */
  | 'received'
  | 'delivered'
  | 'opened'
  | 'starred'
  | 'replied'
  | 'forwarded'
  | 'landed_inbox'
  | 'landed_spam'
  | 'landed_others'
  | 'moved_from_spam_to_inbox'
  | 'moved_from_other_to_inbox'
  | 'undelivered';

/** `GET /:id/activity` day-header summary — counts only, no items. One entry
 * per day that HAS activity: the 5 most recent such days, newest first. Days
 * with nothing in them are omitted, so `date`s are not necessarily
 * consecutive and there can be fewer than 5 entries (none at all for an
 * account that has never warmed). */
export type ActivityDaySummary = {
  /** YYYY-MM-DD, org-local. */
  date: string;
  counts: {
    sent: number;
    /** Emails received that day. The other counts are sender-only. */
    received: number;
    delivered: number;
    /** Mails this account sent that day that got a reply back. Never counts a
     * received email this account replied to. */
    replied: number;
    /** Mails this account sent that day that the recipient forwarded on. */
    forwarded: number;
    rescued: number;
    undelivered: number;
  };
};

export type ActivityDaySummaryResponse = {
  days: ActivityDaySummary[];
  /** IANA zone the days were bucketed in (org timezone, `UTC` fallback). Render activity
   * timestamps in it. Optional only for an API older than this web build. */
  timezone?: string;
  /** Today's org-local calendar date (YYYY-MM-DD) — the reference the
   * Today/Yesterday day-header badge is computed against. It cannot be read
   * off `days[0]`: that is the most recent ACTIVE day, which is an earlier
   * date whenever the account has been idle. Optional only for an API older
   * than this web build, where the badge is simply not rendered rather than
   * guessed from the browser clock. */
  today?: string;
};

/** Fetch the activity day summary (counts only). `engagedOnly` picks WHICH
 * days are listed (the 5 most recent with an engaged thread) — pass the same
 * value as the cycles calls so `dayOffset` names the same day on both. It
 * never filters the counts. There is no window-level pagination. */
export async function getActivityDaySummary(
  id: number,
  engagedOnly: boolean,
): Promise<ActivityDaySummaryResponse> {
  const sp = new URLSearchParams();
  sp.set('engagedOnly', String(engagedOnly));
  return apiFetch<ActivityDaySummaryResponse>(`/email-accounts/${id}/activity?${sp.toString()}`);
}

export type ActivityCycleEvent = {
  eventType: ActivityEventType;
  /** "YYYY-MM-DD HH:mm:ss". */
  eventAt: string;
};

/** What one message in a thread IS. Mirrors the backend's
 * `ACTIVITY_MESSAGE_KIND` (the wire face of `WARMUP_EMAIL_TYPE`). */
export type ActivityMessageKind = 'send' | 'reply' | 'forward';

/** Where this account stands on ONE message of a thread. Mirrors the backend's
 * `ACTIVITY_CYCLE_DIRECTION`. `observed` is a message the viewer is neither
 * sender nor recipient of (B→C, seen by A) — BOTH of its addresses are masked,
 * so there is no "peer" to name. */
export type ActivityViewerRole = 'sent' | 'received' | 'observed';

/** One message (one `warmupEmailHistory` row) inside a thread. Every mailbox
 * the row is visible to gets the same row; only `viewerRole` and the masking
 * differ. (A forward recipient only ever sees its own branch — D8.) */
export type ActivityThreadMessage = {
  /** `WarmupEmailHistory.id`, stringified. */
  warmupHistoryId: string;
  subject: string;
  messageKind: ActivityMessageKind;
  viewerRole: ActivityViewerRole;
  /** This account's own address when it sent this message, else MASKED. */
  senderEmail: string;
  /** This account's own address when it received this message, else MASKED
   * SERVER-SIDE, e.g. "3k********t+3@gmail.com" — do not mask again. */
  recipientEmail: string;
  /** The highest-precedence event present in this message's own `events`. */
  latestEventType: ActivityEventType;
  /** "YYYY-MM-DD HH:mm:ss". */
  latestEventAt: string;
  /** The row's own `bounced` flag — a direct field, not
   * re-derived from `latestEventType`. */
  bounced: boolean;
  /** NEWEST first (the opposite direction from `ActivityThread.messages`, on
   * purpose — the shape mail clients use). */
  events: ActivityCycleEvent[];
};

/** One conversation — the `parentId` tree rooted at a single warm-up send,
 * with every hop this account is a party to or can observe. */
export type ActivityThread = {
  /** The root row's id, stringified. */
  threadId: string;
  /** The first VISIBLE message's subject — the root's for a party to the
   * root, the forward's (`Fwd: …`) for a forward recipient (D8). */
  subject: string;
  messageCount: number;
  /** Headline badge — the highest-precedence event across ALL messages, which
   * is why a thread whose root only landed in the inbox still headlines
   * "Replied" when a child carries the reply. */
  latestEventType: ActivityEventType;
  /** "YYYY-MM-DD HH:mm:ss". */
  latestEventAt: string;
  /** The anchor: the first message in this thread that concerns THIS account.
   * Both the day-bucketing/sort key and the `lastEventAt` cursor key — never
   * `latestEventAt` (sorting on the latest event floats every replied mail to
   * the top and a day reads as "everything got a reply"). */
  startedAt: string;
  /** The `startedAt` message's own id — the same-second cursor tiebreak. */
  anchorHistoryId: string;
  /** OLDEST first, so an expanded thread reads like an email thread. */
  messages: ActivityThreadMessage[];
};

export type ActivityCyclesResponse = {
  threads: ActivityThread[];
  hasMore: boolean;
  /** This page's last thread's ANCHOR time (`startedAt` — the sort key, not
   * its `latestEventAt`, despite the field name) — echo back verbatim as the
   * next `lastEventAt` cursor when `hasMore` is true; `null` iff `hasMore` is
   * `false`. Prefer checking `hasMore` explicitly (its meaning is
   * unambiguous) over relying on this field's nullness. */
  lastEventAt: string | null;
  /** Cursor tiebreak for threads anchored in the same second (the last
   * thread's `anchorHistoryId`); echo back with `lastEventAt`. Optional
   * because an older API omits it. */
  lastHistoryId?: string | null;
};

export type GetActivityCyclesParams = {
  /** Index into the day list the summary returned (0 = the most recent day
   * WITH activity), not a count of days ago — empty days are skipped, so day
   * 1 can be several dates before day 0. Required. */
  dayOffset: number;
  /** Hide threads nobody engaged with (no reply, forward, open, star or spam
   * rescue). Required here even though the server defaults it to `true`, so
   * the request always states its own filter. Send the same value on every
   * "Show more" for a day, or the cursor walks a different list. */
  engagedOnly: boolean;
  /** Page size within the day — counts THREADS, not rows. */
  limit?: number;
  /** Cursor — omit on the first call for a day; on "Show more", pass the
   * previous response's `lastEventAt` verbatim. */
  lastEventAt?: string;
  /** Cursor tiebreak — the previous response's `lastHistoryId`, sent
   * alongside `lastEventAt`. */
  lastHistoryId?: string;
};

/**
 * Fetch one day's threads, cursor-paginated on
 * (`lastEventAt`, `lastHistoryId`).
 * Called lazily — only when the day is expanded, never eagerly for all 5 days
 * on mount (see `useActivityCyclesQuery` in
 * `features/email-accounts/queries.ts`).
 *
 * `engagedOnly` goes on the wire as `String(...)`: the backend's
 * `@Transform` only treats the literal `'true'` (or a real boolean) as true,
 * so a bare `URLSearchParams.set` of anything else would silently read false.
 */
export async function getActivityCycles(
  id: number,
  params: GetActivityCyclesParams,
): Promise<ActivityCyclesResponse> {
  const sp = new URLSearchParams();
  sp.set('dayOffset', String(params.dayOffset));
  sp.set('engagedOnly', String(params.engagedOnly));
  if (params.limit !== undefined) sp.set('limit', String(params.limit));
  if (params.lastEventAt) sp.set('lastEventAt', params.lastEventAt);
  if (params.lastEventAt && params.lastHistoryId) sp.set('lastHistoryId', params.lastHistoryId);
  return apiFetch<ActivityCyclesResponse>(
    `/email-accounts/${id}/activity/cycles?${sp.toString()}`,
  );
}

/** One day's entry in the bulk-cycles response — the existing
 * `ActivityCyclesResponse` shape (`threads`/`hasMore`/`lastEventAt`) plus the
 * `dayOffset` it belongs to, since the bulk endpoint returns all 5 days in a
 * single flat array rather than one response per day. */
export type ActivityCyclesBulkDay = ActivityCyclesResponse & {
  /** This entry's index in `days` (0 = most recent active day). */
  dayOffset: number;
  /** This day's org-local calendar date (YYYY-MM-DD), matching the
   * same-`date` entry of the day summary. Join the two responses on THIS
   * rather than on `dayOffset`: both derive their day list from the data, so
   * a row landing between the two requests can shift what an index means.
   * Optional only for an API older than this web build. */
  date?: string;
};

export type ActivityCyclesBulkResponse = {
  /** One entry per day that HAS activity — the same day list, in the same
   * order, that the day summary returns. */
  days: ActivityCyclesBulkDay[];
};

/**
 * Fetch every surfaced day's first page of threads in a single call —
 * replaces one `getActivityCycles` call per day on page load. Hits the SAME
 * `GET /:id/activity/cycles` route as `getActivityCycles`, just with no
 * `dayOffset` param at all — the backend branches on its absence to return
 * the all-5-days shape instead of a new route (v3 of the plan folded the v2
 * `/bulk` endpoint into this one). "Show more" beyond the first page of any
 * single day still calls `getActivityCycles` directly with `dayOffset` +
 * `lastEventAt`, unchanged. `limit` is passed explicitly, matching every
 * other `getActivityCycles` call site (see `ACTIVITY_CYCLES_INITIAL_LIMIT`'s
 * docstring) rather than silently riding the backend's default.
 */
export async function getActivityCyclesBulk(
  id: number,
  engagedOnly: boolean,
): Promise<ActivityCyclesBulkResponse> {
  const sp = new URLSearchParams();
  sp.set('engagedOnly', String(engagedOnly));
  sp.set('limit', String(ACTIVITY_CYCLES_INITIAL_LIMIT));
  return apiFetch<ActivityCyclesBulkResponse>(`/email-accounts/${id}/activity/cycles?${sp.toString()}`);
}

/** Mirrors the backend's `MAILBOX_HISTORY_EVENT_TYPE`
 * (`apps/api/.../constants/activity.constants.ts`) — keep in lock-step, same
 * rationale as `ActivityEventType` above. `MailboxHistoryEvent.eventType` stays
 * a plain `string` on the wire so a newer API's unknown value degrades to
 * `mailboxHistoryEventMeta`'s fallback instead of breaking the panel. */
export type MailboxHistoryEventType =
  | 'mailbox_connected'
  | 'mailbox_disconnected'
  | 'warmup_started'
  /** A suspension released onto the throttle ladder — sending restarts at reduced volume. */
  | 'warmup_resumed_reduced'
  | 'warmup_paused'
  | 'warmup_throttled'
  /** An already-throttled account whose limit was cut again. */
  | 'warmup_throttle_tightened'
  | 'warmup_throttle_lifted'
  | 'warmup_suspended_spam'
  | 'warmup_suspended_bounce'
  /** A rate-driven bounce suspension — resumes on its own ("Cooling down"). */
  | 'warmup_cooldown_bounce'
  | 'warmup_suspended_blacklist'
  | 'warmup_suspended_auth'
  | 'warmup_suspended_provider'
  | 'warmup_suspended_platform'
  | 'warmup_suspended_verification'
  | 'dns_check_passed'
  | 'dns_check_failed';

/** One "Recent Mailbox History" row — an account-lifecycle event (connect,
 * disconnect, warmup start/pause/throttle/suspend, DNS check). Label/icon lookup is
 * entirely client-side in v2 (see `mailboxHistoryEventMeta` in
 * `features/email-accounts/activity-mappers.ts`) — the backend no longer
 * supplies `label`/`iconKey`. */
export type MailboxHistoryEvent = {
  eventType: string;
  /** "YYYY-MM-DD HH:mm:ss". */
  eventAt: string;
  /** Why warm-up was stopped or slowed, in plain language — shown when the row
   * is clicked. Only pause/throttle/suspend events carry one, and only rows
   * recorded after reasons were saved; `null` otherwise. */
  reason: string | null;
};

export type MailboxHistoryResponse = {
  events: MailboxHistoryEvent[];
};

/** The `limit` server-side cap for "Recent Mailbox History" — matches the
 * backend's `MAILBOX_HISTORY_LIMIT_DEFAULT`. Single source of truth for every
 * call site (`getMailboxHistory`'s default, `useMailboxHistoryQuery`'s
 * default, and the route loader's prefetch) so the query key + the request
 * can never drift apart into a cache-miss/duplicate-fetch. */
export const MAILBOX_HISTORY_LIMIT = 20;

/** First-page size for one day's activity threads — used by the bulk
 * all-5-days fetch on tab load and by a single day's own first-page fetch.
 * Kept smaller than `ACTIVITY_CYCLES_LIMIT` so the tab opens compact; the
 * cursor-based "Show more" (`lastEventAt`/`lastHistoryId`) doesn't care that
 * the two page sizes differ. */
export const ACTIVITY_CYCLES_INITIAL_LIMIT = 5;

/** Page size for each "Show more" continuation within one day — matches the
 * backend's `DEFAULT_CYCLES_LIMIT`. Both limits are passed explicitly on every
 * `getActivityCycles` call rather than left implicit, so the request always
 * states its own page size instead of silently riding whatever the backend's
 * default happens to be. */
export const ACTIVITY_CYCLES_LIMIT = 10;

/** Fetch the curated, bounded "Recent Mailbox History" list (`limit` caps the
 * count server-side, default `MAILBOX_HISTORY_LIMIT`). */
export async function getMailboxHistory(
  id: number,
  limit: number = MAILBOX_HISTORY_LIMIT,
): Promise<MailboxHistoryResponse> {
  return apiFetch<MailboxHistoryResponse>(`/email-accounts/${id}/mailbox-history?limit=${limit}`);
}

/** Every stored mailbox-history event, no limit — the history download. The
 * panel itself stays on `getMailboxHistory`'s 20. */
export async function exportMailboxHistory(id: number): Promise<MailboxHistoryResponse> {
  return apiFetch<MailboxHistoryResponse>(`/email-accounts/${id}/mailbox-history/export`);
}
